// The ⌘K palette's record search: GET /api/search?q= (apps/server/src/search.mjs).
// Parsing is here, free of React and auth, so it is unit tested
// (recordSearch.test.ts); useRecordSearch runs it as the query changes.
import { useCallback, useEffect, useRef, useState } from 'react';

import { isObjectKey } from '../objects/routes';
import type { ObjectKey } from '../navigation';

export interface RecordResult {
  object: ObjectKey;
  id: string;
  label: string;
  detail: string | null;
}

export const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 150;

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export function searchUrl(query: string): string {
  return `/api/search?${new URLSearchParams({ q: query.trim() })}`;
}

/** The results, dropping any row that does not name a known object and an id. */
export function parseSearchResults(payload: unknown): RecordResult[] {
  const rows = (payload as { results?: unknown } | null)?.results;
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row: Record<string, unknown>) =>
    typeof row?.object === 'string' && isObjectKey(row.object) && typeof row.id === 'string' && row.id !== ''
      ? [{
          object: row.object,
          id: row.id,
          label: typeof row.label === 'string' && row.label.trim() ? row.label : 'Untitled',
          detail: typeof row.detail === 'string' && row.detail ? row.detail : null,
        }]
      : [],
  );
}

/**
 * Searches as the query changes, after a short pause, and drops answers to
 * queries that are no longer current. A failed search shows no results.
 */
export function useRecordSearch(fetcher: Fetch) {
  const [results, setResults] = useState<RecordResult[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef('');
  const controller = useRef<AbortController | null>(null);

  const search = useCallback(
    (query: string) => {
      const q = query.trim();
      latest.current = q;
      window.clearTimeout(timer.current);
      controller.current?.abort();
      if (q.length < MIN_QUERY_LENGTH) {
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      timer.current = window.setTimeout(async () => {
        const abort = new AbortController();
        controller.current = abort;
        try {
          const response = await fetcher(searchUrl(q), { signal: abort.signal });
          const found = response.ok ? parseSearchResults(await response.json()) : [];
          if (latest.current === q) setResults(found);
        } catch {
          if (latest.current === q) setResults([]);
        } finally {
          if (latest.current === q) setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [fetcher],
  );

  useEffect(() => () => {
    window.clearTimeout(timer.current);
    controller.current?.abort();
  }, []);

  return { results, loading, search };
}
